import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { PriceConfig, Product } from '../../../types';
import { AvailableCurrenciesService } from '../../../../../zalamo/currencies/available-currencies.service';
import { calculateProductPriceFromParams } from '../../../products-front/helpers';
import { map } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import {
  listFilePriceEntitlements,
  ProductInBasket,
  ShoppingCartState,
} from 'ZalamoLibrary/src/lib/ui/products/shopping-cart-back/actions/standalone';
import {
  selectFilePrices,
  selectFilesInBasket,
  selectPackagesInBasket,
  selectPhotoGroups,
  selectPhotosList,
  selectPriceToAddToEveryFileInProduct,
  selectPriceToAddToFirstPrint,
  selectPrintsInBasket,
  selectProductsInBasket,
  selectProductsList,
} from 'ZalamoLibrary/src/lib/ui/products/shopping-cart-back/actions/selector';
import { combineLatest } from 'rxjs';
import { singleStepPriceCalculator } from '@ui/step-pricing/StepPriceCalculator';
import { StepPricingMode } from '../../../../../../../../photographer-panel/src/graphql';
import { ConfigPickerFromPhotographerPreConfig } from '@ui/products/products-front/components/config-picker-from-photographer-pre-config/types';

@Pipe({
    name: 'priceWithConfigParser',
    standalone: false
})
export class PriceWithConfigParserPipe implements PipeTransform {
  constructor(
    private availableCurrencies: AvailableCurrenciesService,
    @Inject(LOCALE_ID) private localeId,
    private store: Store<{ shoppingCart: ShoppingCartState }>,
  ) {}

  transform(
    product: Product,
    configuration: { [x: string]: string | boolean | number },
    photoIdList: Array<{ photoId: string }>,
    typePrice: 'base' | 'with file',
    quantity?: number,
    productInBasketId?: string,
    photographerPick?: ConfigPickerFromPhotographerPreConfig,
    isFree = false,
  ) {
    return combineLatest([
      this.store.pipe(select(selectProductsInBasket)),
      this.store.pipe(select(selectPriceToAddToEveryFileInProduct)),
      this.store.pipe(select(selectPhotosList)),
      this.store.pipe(select(selectPhotoGroups)),
      combineLatest([
        this.store.pipe(select(selectFilesInBasket)),
        this.store.pipe(select(selectFilePrices)),
        this.store.pipe(select(selectPriceToAddToFirstPrint)),
        this.store.pipe(select(selectPrintsInBasket)),
        this.store.pipe(select(selectPackagesInBasket)),
      ]),
    ]).pipe(
      map(
        ([
          productsInBasket,
          priceToAddToEveryFileInProduct,
          photosList,
          photoGroups,
          [
            filesInBasket,
            filePrices,
            priceToAddToFirstPrint,
            printsInBasket,
            packagesInBasket,
          ],
        ]) => {
          const code = this.availableCurrencies.getCurrentCurrency();
          const price: Array<PriceConfig> = [];
          price.push({
            price:
              calculateProductPriceFromParams(
                product,
                configuration,
                code,
                photoIdList.length,
                photographerPick,
                isFree,
              ) * (quantity || 1),
            code,
          });
          if (
            typePrice === 'with file' ||
            (typePrice === 'base' &&
              product.imagesRequired &&
              product.requiredImages.max === 1 &&
              !product.config.length)
          ) {
            const productInBasketIndex = productsInBasket.findIndex(
              ({ selectedProductId }) =>
                selectedProductId === productInBasketId,
            );
            const existingProduct = productsInBasket[productInBasketIndex];
            const latestBasketTime = [
              ...productsInBasket.map(
                ({ timeAddedToShoppingCart }) => timeAddedToShoppingCart,
              ),
              ...filesInBasket.map(
                ({ timeAddedToShoppingCart }) => timeAddedToShoppingCart,
              ),
              ...printsInBasket.map(
                ({ timeAddedToShoppingCart }) => timeAddedToShoppingCart,
              ),
            ].reduce((latest, time) => Math.max(latest, time ?? 0), 0);
            const projectedProduct: ProductInBasket = existingProduct
              ? {
                  ...existingProduct,
                  configuration,
                  photoIdList,
                  quantity: quantity || existingProduct.quantity,
                }
              : {
                  nameClientProduct: '',
                  productId: product.productId,
                  selectedProductId:
                    productInBasketId ?? '__file-price-product-preview__',
                  quantity: quantity || 1,
                  configuration,
                  timeAddedToShoppingCart: latestBasketTime + 1,
                  photoIdList,
                  finished: { allImages: true, allOptions: true },
                  redBackground: false,
                };
            const projectedProductsInBasket =
              productInBasketIndex >= 0
                ? productsInBasket.map((item, index) =>
                    index === productInBasketIndex ? projectedProduct : item,
                  )
                : productsInBasket.concat(projectedProduct);
            const projectedProductIndex =
              productInBasketIndex >= 0
                ? productInBasketIndex
                : projectedProductsInBasket.length - 1;
            const filePriceEntitlements = listFilePriceEntitlements({
              photoGroups,
              photoGroupList: photosList,
              priceToAddToFirstPrint,
              priceToAddToEveryFileInProduct,
              filePrices,
              printsInBasket,
              filesInBasket,
              productsInBasket: projectedProductsInBasket,
              packagesInBasket,
            });
            const processedPhotoIds = new Set<string>();

            photoIdList.forEach(({ photoId }) => {
              if (processedPhotoIds.has(photoId)) {
                return;
              }
              processedPhotoIds.add(photoId);

              const chargedByEarlierProduct = projectedProductsInBasket.some(
                ({ photoIdList: otherPhotoIds }, index) =>
                  index < projectedProductIndex &&
                  otherPhotoIds.some(
                    ({ photoId: otherPhotoId }) => otherPhotoId === photoId,
                  ),
              );
              if (chargedByEarlierProduct) {
                return;
              }

              const photoGroupId = photosList
                .find(({ photos }) =>
                  photos.some((photo) => photo.photoId === photoId),
                )
                ?.photos.find(
                  (photo) => photo.photoId === photoId,
                )?.photoGroupId;
              const config = photoGroups.find(
                (group) => group.photoGroupId === photoGroupId,
              )?.config;
              const supplement =
                config?.individualSellSettings &&
                config.sellProducts &&
                config.customProductsConfig
                  ? config.priceToAddToEveryFileInProduct
                  : priceToAddToEveryFileInProduct;
              const fileSuppressesSupplement =
                (supplement.skipAddToEveryFileInProductIfFileBought &&
                  filesInBasket.some(
                    (file) =>
                      file.photoId === photoId && !file.togetherWithProduct,
                  )) ||
                (!supplement.skipAddToEveryFileInProductIfFileBought &&
                  filesInBasket.some(
                    (file) =>
                      file.photoId === photoId &&
                      !!file.togetherWithPrint &&
                      !!file.togetherWithLookBookProduct,
                  ));

              if (
                !supplement.addPriceToEveryFileInProduct ||
                fileSuppressesSupplement
              ) {
                return;
              }

              const targetPrice = price.find((item) => item.code === code);
              if (!targetPrice) {
                return;
              }

              if (supplement.countWithFilePrices) {
                const entitlement = filePriceEntitlements.find(
                  (item) => item.photoId === photoId && item.type === 'product',
                );
                if (!entitlement) {
                  return;
                }
                const availableFilePrices =
                  config?.individualSellSettings && config.customFilesPricing
                    ? config.files ?? []
                    : filePrices;
                const filePrice = availableFilePrices.find(
                  ({ filePricingId }) =>
                    filePricingId === entitlement.filePricingId,
                );
                const pricingList = filePriceEntitlements.filter(
                  (item) => item.filePricingId === entitlement.filePricingId,
                );
                const entitlementIndex = pricingList.findIndex(
                  (item) => item.photoId === photoId && item.type === 'product',
                );
                const supplementPrice = singleStepPriceCalculator(
                  entitlementIndex + 1,
                  pricingList.length,
                  !filePrice?.stepPricingEnabled ||
                    filePrice.stepPricing.mode === 'None'
                    ? {
                        ...filePrice?.stepPricing,
                        mode: StepPricingMode.None,
                      }
                    : filePrice.stepPricing,
                  filePrice?.price,
                ).find((item) => item.code === code)?.price;

                targetPrice.price += supplementPrice ?? 0;
              } else {
                targetPrice.price +=
                  supplement.price?.find((item) => item.code === code)?.price ??
                  0;
              }
            });
          }
          return price;
        },
      ),
    );
  }
}
